import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Plus, Search, X, ChevronDown, Edit2, Trash2, Calendar, User, Briefcase } from "lucide-react";
import { apiBaseUrl } from '../../../../../../../services/config';
import { useTheme } from "context/ThemeContext";
import OfferLetterGenerator from "../../interviews/OfferLetterGenerator";

const API = apiBaseUrl;
const ax  = () => ({ headers:{ Authorization:`Bearer ${sessionStorage.getItem("platform_token")||""}` }});

const SOURCES = ["Direct","LinkedIn","Referral","Job Board","Campus","Consultancy","Website","Other"];
const TABS    = ["All","Pipeline","Interview Scheduled","Selected","Rejected","On Hold"];

const EMPTY = {
  first_name:"",last_name:"",email:"",phone:"",position:"",department:"",
  experience_years:0,current_company:"",current_ctc:0,expected_ctc:0,
  notice_period:0,source:"Direct",status:"Pipeline",notes:""
};

export default function ApplicantPipeline() {
  const { T } = useTheme();
  const STATUS_CFG = {
    "Pipeline":            { color:T.cyan,   bg:`${T.cyan}1F`   },
    "Interview Scheduled": { color:T.purple, bg:`${T.purple}1F`  },
    "Selected":            { color:T.green,  bg:`${T.green}1F`   },
    "Rejected":            { color:T.red,    bg:`${T.red}1F`  },
    "On Hold":             { color:T.amber,  bg:`${T.amber}1F`   },
  };
  const inp = (x={}) => ({
    width:"100%",padding:"9px 12px",border:`1px solid ${T.border}`,borderRadius:8,
    background:T.inp_bg,color:T.text,
    fontFamily:"Outfit,sans-serif",fontSize:13,outline:"none",boxSizing:"border-box",...x
  });
  const lbl = { fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:5,
                textTransform:"uppercase",letterSpacing:"0.07em" };

  const [tab,       setTab]       = useState("All");
  const [applicants,setApplicants]= useState([]);
  const [loading,   setLoading]   = useState(false);
  const [search,    setSearch]    = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editRec,   setEditRec]   = useState(null);
  const [form,      setForm]      = useState(EMPTY);
  const [saving,    setSaving]    = useState(false);
  const [toast,     setToast]     = useState("");
  const [delConfirm,setDelConfirm]= useState(null);
  const [offerApplicant, setOfferApplicant] = useState(null); // applicant for offer letter
  const [converting,     setConverting]     = useState(null); // applicant id being converted
  const [converted,      setConverted]      = useState({});   // {id: true} — session-local, same as interviewsList.jsx

  const flash = (m,ok=true) => { setToast({m,ok}); setTimeout(()=>setToast(""),3500); };

  // Mirrors convertToEmployee() in interviewsList.jsx — same payload
  // shape, same POST /employees/ call. "converted" is local UI state
  // only (not persisted server-side), matching that file's own behavior:
  // it resets on reload there too, this isn't a new limitation.
  const convertToEmployee = async (a) => {
    setConverting(a.id);
    try {
      const payload = {
        first_name:      a.first_name,
        last_name:       a.last_name || "",
        email:           a.email || "",
        phone:           a.phone || "",
        date_of_joining: new Date().toISOString().split("T")[0],
        status:          "Active",
      };
      await axios.post(`${API}/employees/`, payload, ax());
      setConverted(prev => ({ ...prev, [a.id]: true }));
      flash(`✅ ${a.first_name} ${a.last_name} added as employee`);
    } catch(e) {
      flash("❌ " + (e.response?.data?.detail || "Conversion failed"), "err");
    } finally { setConverting(null); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab !== "All") params.status = tab;
      if (search)        params.search = search;
      const r = await axios.get(`${API}/applicants/`, { params, ...ax() });
      setApplicants(r.data || []);
    } catch(e) { flash("Failed to load","err"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab, search]);

  const openAdd = () => { setEditRec(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (a) => {
    setEditRec(a);
    setForm({ first_name:a.first_name,last_name:a.last_name,email:a.email||"",
      phone:a.phone||"",position:a.position||"",department:a.department||"",
      experience_years:a.experience_years||0,current_company:a.current_company||"",
      current_ctc:a.current_ctc||0,expected_ctc:a.expected_ctc||0,
      notice_period:a.notice_period||0,source:a.source||"Direct",
      status:a.status||"Pipeline",notes:a.notes||"" });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.first_name||!form.last_name) return flash("First and last name required","err");
    setSaving(true);
    try {
      if (editRec) {
        await axios.put(`${API}/applicants/${editRec.id}`, form, ax());
        flash("✅ Applicant updated");
      } else {
        await axios.post(`${API}/applicants/`, form, ax());
        flash("✅ Applicant added to pipeline");
      }
      setModalOpen(false); load();
    } catch(e) { flash(e.response?.data?.detail||"Save failed","err"); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/applicants/${id}/status?status=${encodeURIComponent(status)}`, {}, ax());
      setApplicants(p => p.map(a => a.id===id ? {...a,status} : a));
    } catch(e) { flash("Status update failed","err"); }
  };

  const del = async (id) => {
    try {
      await axios.delete(`${API}/applicants/${id}`, ax());
      flash("✅ Applicant removed");
      setDelConfirm(null); load();
    } catch(e) { flash("Delete failed","err"); }
  };

  // Counts per tab
  const counts = TABS.reduce((acc,t) => {
    acc[t] = t==="All" ? applicants.length : applicants.filter(a=>a.status===t).length;
    return acc;
  }, {});

  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  return (
    <div style={{fontFamily:"Outfit,sans-serif",color:T.text}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontSize:11,color:T.cyan,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>RECRUITMENT</div>
          <h2 style={{fontSize:22,fontWeight:800,margin:"0 0 4px"}}>Applicant Pipeline</h2>
          <p style={{fontSize:13,color:T.muted,margin:0}}>Track candidates from application to hire</p>
        </div>
        <button onClick={openAdd} style={{display:"flex",alignItems:"center",gap:7,padding:"10px 18px",borderRadius:9,border:"none",background:`linear-gradient(135deg,${T.cyan},${T.purple})`,color:"#fff",fontFamily:"Outfit,sans-serif",fontSize:13,fontWeight:800,cursor:"pointer"}}>
          <Plus size={14}/> Add Candidate
        </button>
      </div>

      {/* Summary cards */}
      <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
        {[
          {l:"Total",   v:applicants.length,  c:T.text},
          {l:"Pipeline",v:applicants.filter(a=>a.status==="Pipeline").length,           c:T.cyan},
          {l:"Scheduled",v:applicants.filter(a=>a.status==="Interview Scheduled").length,c:T.purple},
          {l:"Selected", v:applicants.filter(a=>a.status==="Selected").length,           c:T.green},
          {l:"Rejected", v:applicants.filter(a=>a.status==="Rejected").length,           c:T.red},
          {l:"On Hold",  v:applicants.filter(a=>a.status==="On Hold").length,            c:T.amber},
        ].map(({l,v,c})=>(
          <div key={l} style={{padding:"12px 18px",background:T.card,border:`1px solid ${T.border}`,borderRadius:10,flex:1,minWidth:100,textAlign:"center"}}>
            <div style={{fontSize:22,fontWeight:900,color:c}}>{v}</div>
            <div style={{fontSize:10,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",marginTop:3}}>{l}</div>
          </div>
        ))}
      </div>

      {/* Search + Tabs */}
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,overflow:"hidden"}}>
        {/* Search bar */}
        <div style={{padding:"12px 16px",borderBottom:`1px solid ${T.border}`,display:"flex",gap:10,alignItems:"center"}}>
          <div style={{position:"relative",flex:1,maxWidth:320}}>
            <Search size={14} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:T.dim}}/>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search by name, email, position…"
              style={{...inp({paddingLeft:32}),width:"100%"}}/>
          </div>
          {search && <button onClick={()=>setSearch("")} style={{background:"none",border:"none",color:T.muted,cursor:"pointer"}}><X size={14}/></button>}
        </div>

        {/* Status tabs */}
        <div style={{display:"flex",borderBottom:`1px solid ${T.border}`,padding:"0 16px",gap:2,overflowX:"auto"}}>
          {TABS.map(t=>{
            const sc = STATUS_CFG[t]||{color:T.cyan};
            const active = tab===t;
            return (
              <button key={t} onClick={()=>setTab(t)}
                style={{padding:"10px 14px",border:"none",borderBottom:`2px solid ${active?(STATUS_CFG[t]?.color||T.cyan):"transparent"}`,background:"none",color:active?(STATUS_CFG[t]?.color||T.cyan):T.muted,cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:12.5,fontWeight:active?700:400,whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:6}}>
                {t}
                <span style={{padding:"1px 6px",borderRadius:10,fontSize:10,fontFamily:"monospace",background:active?`${sc.color}20`:T.toggle_bg,color:active?sc.color:T.dim}}>
                  {tab==="All" || t!=="All" ? applicants.filter(a=>t==="All"||a.status===t).length : applicants.length}
                </span>
              </button>
            );
          })}
        </div>

        {/* Table */}
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr style={{background:T.card2}}>
                {["Candidate","Position","Contact","Experience","Source","Status","Added","Actions"].map(h=>(
                  <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:10,color:T.dim,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${T.border}`,whiteSpace:"nowrap"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} style={{padding:40,textAlign:"center",color:T.muted}}>Loading…</td></tr>}
              {!loading && applicants.length===0 && (
                <tr><td colSpan={8} style={{padding:48,textAlign:"center",color:T.dim}}>
                  <div style={{fontSize:32,marginBottom:12}}>👤</div>
                  <div style={{fontSize:14,fontWeight:600}}>No candidates found</div>
                  <div style={{fontSize:13,marginTop:4,color:T.dim}}>Add your first candidate using the button above</div>
                </td></tr>
              )}
              {applicants.map(a=>{
                const sc = STATUS_CFG[a.status]||STATUS_CFG["Pipeline"];
                return (
                  <tr key={a.id} style={{borderBottom:`1px solid ${T.border}`}}
                    onMouseEnter={e=>e.currentTarget.style.background=T.sidebar_hover}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <td style={{padding:"12px 14px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <div style={{width:36,height:36,borderRadius:"50%",background:`linear-gradient(135deg,${T.cyan},${T.purple})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:"#fff",flexShrink:0}}>
                          {a.first_name[0]}{a.last_name[0]}
                        </div>
                        <div>
                          <div style={{fontSize:13,fontWeight:700,color:T.text}}>{a.first_name} {a.last_name}</div>
                          {a.current_company && <div style={{fontSize:11,color:T.dim}}>{a.current_company}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{padding:"12px 14px"}}>
                      <div style={{fontSize:13,color:T.text}}>{a.position||"—"}</div>
                      {a.department && <div style={{fontSize:11,color:T.dim}}>{a.department}</div>}
                    </td>
                    <td style={{padding:"12px 14px"}}>
                      <div style={{fontSize:12,color:T.muted}}>{a.email||"—"}</div>
                      <div style={{fontSize:12,color:T.muted}}>{a.phone||"—"}</div>
                    </td>
                    <td style={{padding:"12px 14px",textAlign:"center"}}>
                      <span style={{fontSize:13,fontWeight:700,color:T.text}}>{a.experience_years||0}</span>
                      <span style={{fontSize:10,color:T.dim,marginLeft:3}}>yrs</span>
                    </td>
                    <td style={{padding:"12px 14px"}}>
                      <span style={{padding:"3px 8px",borderRadius:100,fontSize:11,fontWeight:600,background:T.toggle_bg,color:T.muted}}>{a.source||"—"}</span>
                    </td>
                    <td style={{padding:"12px 14px"}}>
                      <StatusDropdown current={a.status} onSelect={s=>updateStatus(a.id,s)}/>
                    </td>
                    <td style={{padding:"12px 14px",fontSize:11,color:T.dim,whiteSpace:"nowrap"}}>
                      {a.created_at ? new Date(a.created_at).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}) : "—"}
                    </td>
                    <td style={{padding:"12px 14px"}}>
                      <div style={{display:"flex",gap:6}}>
                        {a.status==="Selected" && (
                          <>
                            <button
                              onClick={()=>setOfferApplicant(a)}
                              title="Generate Offer Letter"
                              style={{ width:30, height:30, borderRadius:7,
                                border:`1px solid ${T.green}4D`,
                                background:`${T.green}14`,
                                color:T.green, cursor:"pointer",
                                display:"flex", alignItems:"center",
                                justifyContent:"center", fontSize:14 }}>
                              📄
                            </button>
                            {!converted[a.id] ? (
                              <button
                                onClick={()=>convertToEmployee(a)}
                                disabled={converting===a.id}
                                title="Move to Employee"
                                style={{ width:30, height:30, borderRadius:7,
                                  border:`1px solid ${T.purple}4D`,
                                  background:`${T.purple}14`,
                                  color:T.purple, cursor:"pointer",
                                  display:"flex", alignItems:"center",
                                  justifyContent:"center", fontSize:14,
                                  opacity:converting===a.id?.6:1 }}>
                                {converting===a.id ? "⏳" : "👤"}
                              </button>
                            ) : (
                              <span title="Already added as employee"
                                style={{ width:30, height:30, borderRadius:7,
                                  border:`1px solid ${T.green}4D`,
                                  background:`${T.green}14`,
                                  color:T.green, display:"flex",
                                  alignItems:"center", justifyContent:"center",
                                  fontSize:14 }}>
                                ✓
                              </span>
                            )}
                          </>
                        )}
                        <button onClick={()=>openEdit(a)} style={{padding:"5px 10px",borderRadius:7,border:`1px solid ${T.border}`,background:"none",color:T.muted,cursor:"pointer"}}><Edit2 size={12}/></button>
                        <button onClick={()=>setDelConfirm(a.id)} style={{padding:"5px 10px",borderRadius:7,border:`1px solid ${T.red}40`,background:"none",color:T.red,cursor:"pointer"}}><Trash2 size={12}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(4px)"}}
          onClick={e=>{if(e.target===e.currentTarget)setModalOpen(false);}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,width:"100%",maxWidth:680,maxHeight:"90vh",overflowY:"auto",fontFamily:"Outfit,sans-serif"}}>
            {/* Modal header */}
            <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,background:T.card,zIndex:1}}>
              <h3 style={{margin:0,fontSize:17,fontWeight:800,color:T.text}}>{editRec?"Edit Candidate":"Add Candidate to Pipeline"}</h3>
              <button onClick={()=>setModalOpen(false)} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:18}}>✕</button>
            </div>
            <div style={{padding:24}}>
              {/* Row 1: Name */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
                <div><label style={lbl}>First Name *</label><input value={form.first_name} onChange={e=>set("first_name",e.target.value)} style={inp()} placeholder="First name"/></div>
                <div><label style={lbl}>Last Name *</label><input value={form.last_name} onChange={e=>set("last_name",e.target.value)} style={inp()} placeholder="Last name"/></div>
              </div>
              {/* Row 2: Contact */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
                <div><label style={lbl}>Email</label><input value={form.email} onChange={e=>set("email",e.target.value)} style={inp()} placeholder="email@example.com" type="email"/></div>
                <div><label style={lbl}>Phone</label><input value={form.phone} onChange={e=>set("phone",e.target.value)} style={inp()} placeholder="+91 xxxxx xxxxx"/></div>
              </div>
              {/* Row 3: Position */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
                <div><label style={lbl}>Position Applied For</label><input value={form.position} onChange={e=>set("position",e.target.value)} style={inp()} placeholder="e.g. Senior Developer"/></div>
                <div><label style={lbl}>Department</label><input value={form.department} onChange={e=>set("department",e.target.value)} style={inp()} placeholder="e.g. Engineering"/></div>
              </div>
              {/* Row 4: Experience + Current Company */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14,marginBottom:14}}>
                <div><label style={lbl}>Experience (yrs)</label><input value={form.experience_years} onChange={e=>set("experience_years",e.target.value)} style={inp()} type="number" min="0" step="0.5"/></div>
                <div><label style={lbl}>Current Company</label><input value={form.current_company} onChange={e=>set("current_company",e.target.value)} style={inp()} placeholder="Company name"/></div>
                <div><label style={lbl}>Notice Period (days)</label><input value={form.notice_period} onChange={e=>set("notice_period",e.target.value)} style={inp()} type="number" min="0"/></div>
              </div>
              {/* Row 5: CTC */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
                <div><label style={lbl}>Current CTC (₹ LPA)</label><input value={form.current_ctc} onChange={e=>set("current_ctc",e.target.value)} style={inp()} type="number" min="0" step="0.1"/></div>
                <div><label style={lbl}>Expected CTC (₹ LPA)</label><input value={form.expected_ctc} onChange={e=>set("expected_ctc",e.target.value)} style={inp()} type="number" min="0" step="0.1"/></div>
              </div>
              {/* Row 6: Source + Status */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
                <div>
                  <label style={lbl}>Source</label>
                  <select value={form.source} onChange={e=>set("source",e.target.value)} style={inp({cursor:"pointer"})}>
                    {SOURCES.map(s=><option key={s} style={{background:T.opt_bg}}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Pipeline Status</label>
                  <select value={form.status} onChange={e=>set("status",e.target.value)} style={inp({cursor:"pointer"})}>
                    {["Pipeline","Interview Scheduled","Selected","Rejected","On Hold"].map(s=><option key={s} style={{background:T.opt_bg}}>{s}</option>)}
                  </select>
                </div>
              </div>
              {/* Notes */}
              <div style={{marginBottom:20}}>
                <label style={lbl}>Notes</label>
                <textarea value={form.notes} onChange={e=>set("notes",e.target.value)} rows={3} placeholder="Any additional notes about this candidate…" style={{...inp(),resize:"vertical"}}/>
              </div>
              {/* Actions */}
              <div style={{display:"flex",gap:10,justifyContent:"flex-end",paddingTop:8,borderTop:`1px solid ${T.border}`}}>
                <button onClick={()=>setModalOpen(false)} style={{padding:"9px 18px",borderRadius:8,border:`1px solid ${T.border}`,background:"none",color:T.muted,cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13}}>Cancel</button>
                <button onClick={save} disabled={saving} style={{padding:"9px 22px",borderRadius:8,border:"none",background:`linear-gradient(135deg,${T.cyan},${T.purple})`,color:"#fff",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13,fontWeight:800,opacity:saving?0.7:1}}>
                  {saving?"Saving…":editRec?"Update Candidate":"Add to Pipeline"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {delConfirm && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:600,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:T.card,border:`1px solid ${T.red}40`,borderRadius:14,padding:28,width:380,textAlign:"center"}}>
            <div style={{fontSize:32,marginBottom:12}}>🗑️</div>
            <h3 style={{margin:"0 0 8px",fontSize:16,fontWeight:800,color:T.text}}>Remove Candidate?</h3>
            <p style={{fontSize:13,color:T.muted,marginBottom:20}}>This will permanently remove the applicant from the pipeline.</p>
            <div style={{display:"flex",gap:10,justifyContent:"center"}}>
              <button onClick={()=>setDelConfirm(null)} style={{padding:"9px 20px",borderRadius:8,border:`1px solid ${T.border}`,background:"none",color:T.muted,cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13}}>Cancel</button>
              <button onClick={()=>del(delConfirm)} style={{padding:"9px 20px",borderRadius:8,border:"none",background:T.red,color:"white",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13,fontWeight:700}}>Remove</button>
            </div>
          </div>
        </div>
      )}

      {/* Offer Letter Generator — same component interviewsList.jsx uses */}
      {offerApplicant && (
        <OfferLetterGenerator
          iv={offerApplicant}
          authHdr={ax().headers}
          onClose={()=>setOfferApplicant(null)}
          flash={flash}
        />
      )}

      {/* Toast */}
      {toast && <div style={{position:"fixed",bottom:24,right:24,zIndex:700,padding:"12px 20px",borderRadius:10,background:toast.ok?`${T.green}15`:`${T.red}15`,border:`1px solid ${toast.ok?T.green:T.red}40`,color:toast.ok?T.green:T.red,fontSize:13,fontWeight:700}}>{toast.m}</div>}
    </div>
  );
}

// Inline status dropdown
function StatusDropdown({ current, onSelect }) {
  const { T } = useTheme();
  const STATUS_CFG = {
    "Pipeline":            { color:T.cyan,   bg:`${T.cyan}1F`   },
    "Interview Scheduled": { color:T.purple, bg:`${T.purple}1F`  },
    "Selected":            { color:T.green,  bg:`${T.green}1F`   },
    "Rejected":            { color:T.red,    bg:`${T.red}1F`  },
    "On Hold":             { color:T.amber,  bg:`${T.amber}1F`   },
  };
  const [open, setOpen]     = useState(false);
  const [coords, setCoords] = useState({ top:0, left:0, width:180 });
  const btnRef  = useRef(null);
  const menuRef = useRef(null);
  const sc  = STATUS_CFG[current] || STATUS_CFG["Pipeline"];

  // Compute the menu's on-screen position from the trigger button's real
  // bounding box, since it's rendered via a portal (see below) and can no
  // longer rely on being positioned relative to a DOM ancestor.
  const positionMenu = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setCoords({ top: r.bottom + 4, left: r.left, width: Math.max(180, r.width) });
  };

  const toggleOpen = () => {
    if (!open) positionMenu();
    setOpen(o => !o);
  };

  useEffect(() => {
    const handler = e => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Keep the menu glued to the button while open, since scrolling the
  // table (or the page) would otherwise leave a fixed-position menu
  // hovering over the wrong row.
  useEffect(() => {
    if (!open) return;
    window.addEventListener("scroll", positionMenu, true);
    window.addEventListener("resize", positionMenu);
    return () => {
      window.removeEventListener("scroll", positionMenu, true);
      window.removeEventListener("resize", positionMenu);
    };
  }, [open]);

  return (
    <div style={{position:"relative",display:"inline-block"}}>
      <button ref={btnRef} onClick={toggleOpen}
        style={{display:"flex",alignItems:"center",gap:5,padding:"4px 10px",borderRadius:20,border:`1px solid ${sc.color}40`,background:sc.bg,color:sc.color,cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:11,fontWeight:700}}>
        {current} <ChevronDown size={10}/>
      </button>
      {open && createPortal(
        <div ref={menuRef} style={{position:"fixed",top:coords.top,left:coords.left,zIndex:9999,background:T.card2,border:`1px solid ${T.cyan}26`,borderRadius:10,padding:4,minWidth:coords.width,boxShadow:T.shadow}}>
          {["Pipeline","Interview Scheduled","Selected","Rejected","On Hold"].map(s=>{
            const c = STATUS_CFG[s];
            return (
              <button key={s} onClick={()=>{onSelect(s);setOpen(false);}}
                style={{display:"block",width:"100%",padding:"7px 12px",borderRadius:7,border:"none",background:s===current?`${c.color}15`:"none",color:s===current?c.color:T.muted,cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:12,fontWeight:s===current?700:400,textAlign:"left"}}>
                {s}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}